import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { Pagination as AntPagination, Button } from "antd";
import { Orientation } from "@/utils/enum";
import styles from "./Pagination.styles.less";

const Pagination = ({
  page,
  pageSize,
  totalItems,
  showLessItems,
  showQuickJumper,
  showSizeChanger,
  onPageChange,
  onPageSizeChange,
}) => {
  const wrapperRef = useRef(null);

  const [orientation, setOrientation] = useState(Orientation.Horizontal);

  const lastPage = useMemo(() => {
    return Math.ceil(totalItems / pageSize);
  }, [totalItems, pageSize]);

  const { cantGoPreviousPage, cantGoNextPage } = useMemo(() => {
    return {
      cantGoPreviousPage: page <= 1,
      cantGoNextPage: page >= lastPage,
    };
  }, [page, lastPage]);

  const wrapperStyles = useMemo(() => {
    const styles = {
      display: "flex",
      alignItems: "center",
    };

    if (orientation === Orientation.Horizontal) {
      styles.flexDirection = "row";
      styles.justifyContent = "space-between";
    } else {
      styles.flexDirection = "column";
      styles.alignItems = "center";
    }

    return styles;
  }, [orientation]);

  const _onPageChange = useCallback(
    newPage => {
      if (typeof onPageChange === "function") {
        onPageChange(Math.min(Math.max(1, newPage), lastPage));
      }
    },
    [onPageChange, lastPage],
  );

  const _onPageSizeChange = useCallback(
    newPageSize => {
      if (typeof onPageSizeChange === "function") {
        onPageSizeChange(newPageSize);
      }
    },
    [onPageSizeChange],
  );

  const handleOnChange = useCallback(
    (newPage, newPageSize) => {
      if (newPage !== page) {
        _onPageChange(newPage);
      }

      if (newPageSize !== pageSize) {
        _onPageSizeChange(newPageSize);
      }
    },
    [page, pageSize, _onPageChange, _onPageSizeChange],
  );

  const handleGoToNextPage = useCallback(() => {
    _onPageChange(page + 1);
  }, [page, _onPageChange]);

  const handleGoToPreviousPage = useCallback(() => {
    _onPageChange(page - 1);
  }, [page, _onPageChange]);

  const previousButton = useMemo(() => {
    return (
      <Button
        style={{
          borderRadius: "4px 0px 0px 4px",
          width: "100px",
          marginRight: orientation === Orientation.Vertical ? "4px" : 0,
        }}
        disabled={cantGoPreviousPage}
        onClick={handleGoToPreviousPage}
      >
        <ArrowLeftOutlined />
        Previous
      </Button>
    );
  }, [orientation, cantGoPreviousPage, handleGoToPreviousPage]);

  useEffect(() => {
    if (!wrapperRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(([{ contentBoxSize }]) => {
      const { inlineSize: width } = contentBoxSize[0];
      if (width <= 485) {
        setOrientation(Orientation.Vertical);
      } else {
        setOrientation(Orientation.Horizontal);
      }
    });

    resizeObserver.observe(wrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div ref={wrapperRef} style={wrapperStyles}>
      {orientation === Orientation.Horizontal && previousButton}
      {totalItems > 0 && (
        <AntPagination
          showLessItems={showLessItems}
          showQuickJumper={showQuickJumper}
          showSizeChanger={showSizeChanger}
          className={styles.strategyTable}
          total={totalItems}
          current={page}
          pageSize={pageSize}
          onChange={handleOnChange}
        />
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          marginTop: orientation === Orientation.Vertical ? "8px" : 0,
        }}
      >
        {orientation === Orientation.Vertical && previousButton}
        <Button
          style={{
            borderRadius: "0px 4px 4px 0px",
            width: "100px",
            marginLeft: orientation === Orientation.Vertical ? "4px" : 0,
          }}
          disabled={cantGoNextPage}
          onClick={handleGoToNextPage}
        >
          Next
          <ArrowRightOutlined />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
